import React, { useEffect, useMemo, useState } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { CrmBadge, CrmButton, CrmInput, CrmSelect } from '../components/ui';
import CrmListPage from '../components/listing/CrmListPage';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import PageLoader from '../components/ui/PageLoader';
import EntityMultiPicker from '../components/products/editor/EntityMultiPicker';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currencyUtils';
import { getBuildBoxBootstrap, getBuildBoxConfig, saveBuildBoxConfig } from '../services/buildBoxService';
import './build-box-settings.scss';

const EMPTY = {
  name: 'Build Your Box',
  page_key: 'create-fusion',
  enabled: false,
  catalog_mode: 'CATEGORY_FILTER',
  min_items: '',
  max_items: '',
  min_amount: '',
  max_amount: '',
  enforce_exact_items: false,
  allow_duplicate_products: false,
  category_ids: [],
  product_ids: [],
};

function Toggle({ label, description, checked, onChange }) {
  return <div className="build-box-toggle"><div><strong>{label}</strong>{description ? <small>{description}</small> : null}</div><label><input type="checkbox" checked={Boolean(checked)} onChange={onChange} /><span /></label></div>;
}

export default function BuildBoxSettings() {
  const { settings } = useSettings();
  const currency = String(settings?.currency_code || 'INR').toUpperCase();
  const [bootstrap, setBootstrap] = useState(null);
  const [storefrontId, setStorefrontId] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    (async () => {
      try {
        const data = await getBuildBoxBootstrap();
        setBootstrap(data);
        const first = data?.storefronts?.[0];
        if (first) setStorefrontId(String(first.id));
      } catch (error) {
        setNotification({ open: true, message: error?.response?.data?.error || 'Failed to load catalog configuration.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!storefrontId || !bootstrap) return;
    (async () => {
      setLoading(true);
      try {
        const storefront = bootstrap.storefronts.find((item) => String(item.id) === String(storefrontId));
        const result = await getBuildBoxConfig(storefront?.code || storefrontId);
        setForm({ ...EMPTY, ...(result.config || {}) });
        setDirty(false);
      } catch (error) {
        setNotification({ open: true, message: error?.response?.data?.error || 'Failed to load Build Your Box settings.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [storefrontId, bootstrap]);

  const storefront = bootstrap?.storefronts?.find((item) => String(item.id) === String(storefrontId));
  const categories = useMemo(() => (bootstrap?.storefrontCategories || []).filter((item) => String(item.storefront_id) === String(storefrontId)), [bootstrap, storefrontId]);
  const products = useMemo(() => (bootstrap?.storefrontListings || []).filter((item) => String(item.storefront_id) === String(storefrontId)).map((item) => ({ ...item, name: item.display_name })), [bootstrap, storefrontId]);

  const change = (patch) => { setForm((previous) => ({ ...previous, ...patch })); setDirty(true); };
  const moveCategory = (categoryId, offset) => {
    const ids = [...(form.category_ids || [])].map(Number);
    const index = ids.findIndex((id) => id === Number(categoryId));
    const nextIndex = index + offset;
    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return;
    [ids[index], ids[nextIndex]] = [ids[nextIndex], ids[index]];
    change({ category_ids: ids });
  };

  const save = async () => {
    if (!storefront) return;
    setSaving(true);
    try {
      const result = await saveBuildBoxConfig(storefront.code || storefront.id, form);
      setForm({ ...EMPTY, ...(result.config || {}) });
      setDirty(false);
      setNotification({ open: true, message: 'Build Your Box configuration saved.', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to save Build Your Box settings.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !bootstrap) return <PageLoader message="Loading Build Your Box…" minHeight={420} />;

  return (
    <CrmListPage
      title="Build Your Box"
      subtitle="Configure the custom box builder without hardcoding Fusion Box category flags."
      hideSavedViews
      hideToolbar
      hideFooter
      headerActions={(
        <>
          <CrmBadge tone={form.enabled ? 'success' : 'neutral'}>{form.enabled ? 'Enabled' : 'Disabled'}</CrmBadge>
          <CrmButton variant="primary" startIcon={<SaveOutlinedIcon />} disabled={!dirty || saving} loading={saving} onClick={save}>
            Save
          </CrmButton>
        </>
      )}
    >
      <div className="build-box-settings">
        <section className="build-box-card">
          <div className="build-box-grid">
            <CrmSelect label="Storefront" value={storefrontId} onChange={(event) => setStorefrontId(event.target.value)}>{(bootstrap?.storefronts || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</CrmSelect>
            <CrmInput label="Experience Name" value={form.name || ''} onChange={(event) => change({ name: event.target.value })} />
            <CrmInput label="Page Key" helperText="The custom-coded page consumes this configuration through the public Build Box API." value={form.page_key || ''} onChange={(event) => change({ page_key: event.target.value })} />
          </div>
          <Toggle label="Enable Build Your Box" description="Makes this builder configuration available to the selected storefront." checked={form.enabled} onChange={(event) => change({ enabled: event.target.checked })} />
        </section>

        <section className="build-box-card">
          <div className="build-box-card__heading"><div><h2>Selection Rules</h2><p>Control how many products/custom selections a customer can put into one box.</p></div></div>
          <div className="build-box-grid">
            <CrmInput type="number" min="0" step="1" label="Minimum Items" value={form.min_items ?? ''} onChange={(event) => { const value = event.target.value === '' ? '' : Math.max(0, Math.round(Number(event.target.value) || 0)); change(form.enforce_exact_items ? { min_items: value, max_items: value } : { min_items: value }); }} />
            <CrmInput type="number" min="0" step="1" label="Maximum Items" value={form.max_items ?? ''} onChange={(event) => { const value = event.target.value === '' ? '' : Math.max(0, Math.round(Number(event.target.value) || 0)); change(form.enforce_exact_items ? { min_items: value, max_items: value } : { max_items: value }); }} />
            <CrmInput type="number" min="0" step="1" label={`Minimum Amount (${currency})`} value={form.min_amount ?? ''} onChange={(event) => change({ min_amount: event.target.value === '' ? '' : Math.max(0, Math.round(Number(event.target.value) || 0)) })} />
            <CrmInput type="number" min="0" step="1" label={`Maximum Amount (${currency})`} value={form.max_amount ?? ''} onChange={(event) => change({ max_amount: event.target.value === '' ? '' : Math.max(0, Math.round(Number(event.target.value) || 0)) })} />
          </div>
          <div className="build-box-toggle-stack">
            <Toggle label="Exact item count" description="When enabled, Minimum Items and Maximum Items stay equal and the storefront requires exactly that count." checked={form.enforce_exact_items} onChange={(event) => change(event.target.checked ? { enforce_exact_items: true, max_items: form.min_items !== '' ? form.min_items : form.max_items } : { enforce_exact_items: false })} />
            <Toggle label="Allow duplicate products" description="Allow the same product to occupy more than one slot in a custom box." checked={form.allow_duplicate_products} onChange={(event) => change({ allow_duplicate_products: event.target.checked })} />
          </div>
          {(form.min_amount || form.max_amount) ? <div className="build-box-summary">Amount rule: {form.min_amount ? formatCurrency(form.min_amount, currency) : 'No minimum'} → {form.max_amount ? formatCurrency(form.max_amount, currency) : 'No maximum'}</div> : null}
        </section>

        <section className="build-box-card">
          <div className="build-box-card__heading"><div><h2>Available Products</h2><p>Choose how the custom-coded page receives its eligible catalog.</p></div></div>
          <CrmSelect label="Catalog Source" value={form.catalog_mode || 'CATEGORY_FILTER'} onChange={(event) => change({ catalog_mode: event.target.value })}>
            <option value="CATEGORY_FILTER">Products from selected storefront categories</option>
            <option value="SELECTED_PRODUCTS">Only specifically selected storefront products</option>
            <option value="ALL">All published storefront products</option>
          </CrmSelect>
          {form.catalog_mode === 'CATEGORY_FILTER' ? <>
            <EntityMultiPicker label="Build Box Categories" options={categories} value={form.category_ids || []} onChange={(category_ids) => change({ category_ids: category_ids.map(Number) })} helperText="These are storefront categories, not the old TFB/core category flags." />
            {(form.category_ids || []).length ? <div className="build-box-category-order">
              <strong>Storefront order</strong>
              {(form.category_ids || []).map(Number).map((categoryId, index) => {
                const category = categories.find((item) => Number(item.id) === categoryId);
                if (!category) return null;
                return <div className="build-box-category-order__row" key={categoryId}>
                  <span className="build-box-category-order__number">{index + 1}</span>
                  <span>{category.name}</span>
                  <div>
                    <CrmButton type="button" size="small" variant="tertiary" disabled={index === 0} onClick={() => moveCategory(categoryId, -1)} aria-label={`Move ${category.name} up`}>↑</CrmButton>
                    <CrmButton type="button" size="small" variant="tertiary" disabled={index === form.category_ids.length - 1} onClick={() => moveCategory(categoryId, 1)} aria-label={`Move ${category.name} down`}>↓</CrmButton>
                  </div>
                </div>;
              })}
            </div> : null}
          </> : null}
          {form.catalog_mode === 'SELECTED_PRODUCTS' ? <EntityMultiPicker label="Build Box Products" options={products} value={form.product_ids || []} onChange={(product_ids) => change({ product_ids: product_ids.map(Number) })} helperText="Use this when the page should expose only a curated set of products." /> : null}
          {form.catalog_mode === 'ALL' ? <div className="build-box-summary">Every published product on {storefront?.name || 'this storefront'} is eligible.</div> : null}
        </section>

        <section className="build-box-card build-box-card--api">
          <h2>Frontend Contract</h2>
          <p>Your custom-coded page can now load its rules and eligible catalog from:</p>
          <code>/api/public/storefronts/{storefront?.code || '{storefront}'}/build-box</code>
          <p>This means the Nuxt page remains custom designed, but its limits, category/product eligibility and enablement are controlled from this dashboard.</p>
        </section>
      </div>

      <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
    </CrmListPage>
  );
}
